import React, {useMemo} from 'react';
import {GraphQLFormattedError} from 'graphql';
import {Operation, ApolloProvider} from '@apollo/client';

import type {AppStore} from './state/store';
import {refreshAccessToken} from './state/auth';
import {useAppStore} from './state/hooks';
import {Role} from './net/types/graphql';
import {configureApolloClient} from './net/apolloClient';
import {useBrowserId} from './BrowserId';
import * as log from './util/log';
import * as netLogger from './net/logger';
import {useToast} from './components/ui/toast';

// Configure the net package to share the same logger as the rest of the app.
netLogger.setLogger(log);

/**
 * Some GraphQL errors are expected and should just be ignored.
 */
const suppressError = (e: GraphQLFormattedError, operation: Operation) => {
  // NOTE: this error happens when the client information disappears from the
  // upstream organization CMS. It's fairly common, and it will result in the
  // UI missing information about this client. Don't annoy the user with banner
  // notifications about it, since there's nothing we can do.
  const wrongNumberOfClientsErr = /wrong number of clients/i;
  const noClientWithIdErr = /^No client with ID:/i;
  const noNativeIdErr = /no native ID registered/i;
  const noClientErr = /client not found/i;
  const noStaffEmailErr = /can't query staff without email/i;
  const clientNotFoundErr = /client not found/i;

  switch (operation.operationName) {
    case 'Chat':
      return clientNotFoundErr.test(e.message);
    case 'GetMessage':
      return (
        wrongNumberOfClientsErr.test(e.message) || noClientErr.test(e.message)
      );
    case 'ClientInfo':
      return (
        noClientWithIdErr.test(e.message) ||
        noClientErr.test(e.message) ||
        noNativeIdErr.test(e.message) ||
        noStaffEmailErr.test(e.message)
      );
    case 'AllClients':
      return (
        noClientWithIdErr.test(e.message) ||
        noClientErr.test(e.message) ||
        noNativeIdErr.test(e.message) ||
        wrongNumberOfClientsErr.test(e.message)
      );
    case 'AllStaff':
      return noStaffEmailErr.test(e.message);
    default:
      return false;
  }
};

/**
 * Hook to get a configured Apollo Client.
 */
export const useConfiguredApolloClient = (
  store: AppStore,
  showToast: (
    message: string,
    type: 'error' | 'success' | 'info' | 'warning',
  ) => void,
) => {
  const browserId = useBrowserId();
  return useMemo(
    () =>
      configureApolloClient({
        getBaseUri() {
          const {baseUrl} = store.getState();
          return baseUrl.value;
        },
        getAuthTokenInfo() {
          const {auth} = store.getState();
          return {
            expiresAt: auth.expiresAt,
            accessToken: auth.token,
            accessTokenType: auth.tokenType,
            refreshToken: auth.refreshToken || undefined,
          };
        },
        async refreshAuthToken() {
          const {
            auth: {refreshToken},
          } = store.getState();

          if (!refreshToken) {
            log.warn('No refresh token available');
            throw new Error('Logging you out');
          }

          const result = await store.dispatch(
            refreshAccessToken({
              refreshToken,
              deviceId: browserId,
            }),
          );
          if (result.meta.requestStatus !== 'fulfilled') {
            throw new Error('Unable to keep you logged in');
          }

          return this.getAuthTokenInfo();
        },
        handleNetworkError(e) {
          log.warn('Network Error', e);
          showToast(
            `We're having issues communicating with the server: ${e.message}`,
            'error',
          );
        },
        handleAuthError(e) {
          // If we're not already on the logout page, go there.
          // It's possible we hit an auth error *on* the logout page,
          // since the logout page tries to make some requests to clean up.
          // We will just swallow those errors.
          if (window.location.pathname !== '/logout') {
            log.error('Auth Error', e);
            showToast(`Your credentials are invalid: ${e.message}`, 'error');
            window.location.pathname = '/logout';
          }
        },
        handleGraphqlError(e, operation) {
          const {auth} = store.getState();
          // Suppress all messages for regular users, since they are confusing.
          if (!auth.roles.includes(Role.Admin)) {
            log.warn('GraphQL Error:', e.message);
          } else if (!suppressError(e, operation)) {
            log.warn('GraphQL Error:', e.message);
            showToast(`An error occurred fetching data: ${e.message}`, 'error');
          } else if (__DEV__) {
            log.warn('[GraphQL Error] [SUPPRESSED]', e.message);
          }

          // This function handles every error, so just return true.
          return true;
        },
      }),
    [store, showToast],
  );
};

export type NetworkProviderProps = {
  children?: React.ReactNode;
};

/**
 * Provide connectivity to the network via a configured Apollo client.
 */
export const NetworkProvider = ({children}: NetworkProviderProps) => {
  const store = useAppStore();
  const {showToast} = useToast();
  const apolloClient = useConfiguredApolloClient(store, showToast);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
